import { useCallback, useState, useMemo } from 'react';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import I18n from 'I18n';
import BreakdownEditorSelect from '../components/BreakdownEditorSelect';
import { useRelationalReportData, useRelationalReportInfrastructure, useRelationalReportProperties, useRelationalReportAdvancedProperties } from '../../../contexts/RelationalReportContext';
import useSRVSnowflakeUpdater from '../../../hooks/relationalReports/useSRVSnowflakeUpdater';
import { getPropertyValue } from '../../../lib/relational-report-utils/property-utils';
import { buildAddColumnParams, buildRemoveColumnParams, buildUpdateColumnParams, getBreakdownChannel } from '../../../lib/relational-report-utils/snowflake-updater-utils';
import getMeasureSizeRanges from '../../../lib/report-utils/getMeasureSizeRanges';
import ErrorAlert from '../../shared-components/ErrorAlert';
import { getIsDropdownDisabled, getCurrentBreakdownColumn } from '../utils';
import { jsx as _jsx } from "react/jsx-runtime";
const RelationalReportBreakdownEditorDropdown = props => {
  const {
    dropdownFooter,
    onChange
  } = props;
  const [error, setError] = useState(null);
  const {
    relationalReport,
    reportColumns: {
      allAvailableColumns,
      allStagedColumns
    },
    isLoading
  } = useRelationalReportData();
  const {
    fullTableAndPropertyMeta: {
      data: tableAndPropertyMeta,
      error: fullTableAndPropertyMetaError
    }
  } = useRelationalReportInfrastructure();
  const {
    availablePropertiesOptionsWithoutDefaultMeasures
  } = useRelationalReportProperties();
  const {
    allDatasetFields
  } = useRelationalReportAdvancedProperties();
  const breakdownChannel = useMemo(() => getBreakdownChannel(relationalReport), [relationalReport]);
  const {
    addColumnToChannel,
    moveColumnToStagedColumns,
    removeColumnFromReportColumns,
    updateEncodedColumnWithExpressionAddStaged,
    updateEncodedColumnWithExpression
  } = useSRVSnowflakeUpdater(props.report);
  const {
    minDimensions
  } = getMeasureSizeRanges(relationalReport);
  const {
    isDisabled,
    disabledReason
  } = getIsDropdownDisabled(relationalReport);
  const currentBreakdownColumn = getCurrentBreakdownColumn(relationalReport);
  const propertyValue = useMemo(() => {
    return currentBreakdownColumn ? getPropertyValue(currentBreakdownColumn) : '';
  }, [currentBreakdownColumn]);
  const onBreakdownChange = useCallback(({
    target: {
      value
    }
  }) => {
    if (!tableAndPropertyMeta) {
      return;
    }
    let result;
    if (!currentBreakdownColumn) {
      // Add new breakdown column
      const {
        column,
        channel
      } = buildAddColumnParams(value, allAvailableColumns, relationalReport, tableAndPropertyMeta, allDatasetFields, breakdownChannel);
      result = addColumnToChannel(column, channel);
    } else if (!value) {
      // Remove breakdown column
      const {
        action,
        params
      } = buildRemoveColumnParams(currentBreakdownColumn, relationalReport, allStagedColumns, tableAndPropertyMeta, allDatasetFields);
      if (action === 'moveToStaged') {
        result = moveColumnToStagedColumns(params.originalColumn, params.newColumnForStagedColumns);
      } else {
        result = removeColumnFromReportColumns(params.column);
      }
    } else {
      // Update existing breakdown column
      const {
        action,
        params
      } = buildUpdateColumnParams(value, currentBreakdownColumn, relationalReport, allAvailableColumns, allStagedColumns, tableAndPropertyMeta, allDatasetFields);
      if (action === 'updateWithStaged') {
        result = updateEncodedColumnWithExpressionAddStaged(params.alias, params.newColumn, params.originalColumn, params.isReplace, params.expressionField);
      } else {
        result = updateEncodedColumnWithExpression(params.alias, params.column, params.isReplace, params.expressionField);
      }
    }
    if (result.error) {
      setError(new Error(result.error));
    } else {
      setError(null);
      onChange(result.updatedReport);
    }
  }, [tableAndPropertyMeta, currentBreakdownColumn, allAvailableColumns, relationalReport, allStagedColumns, allDatasetFields, breakdownChannel, addColumnToChannel, moveColumnToStagedColumns, removeColumnFromReportColumns, updateEncodedColumnWithExpressionAddStaged, updateEncodedColumnWithExpression, onChange]);
  if (isLoading) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  if (fullTableAndPropertyMetaError) {
    return /*#__PURE__*/_jsx(ErrorAlert, {
      errorMessage: I18n.text('report-customization-components.customization-components.relational-report.breakdown-fetch-error'),
      error: fullTableAndPropertyMetaError
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(ErrorAlert, {
      errorMessage: I18n.text('report-customization-components.customization-components.relational-report.breakdown-update-error'),
      error: error
    });
  }
  return /*#__PURE__*/_jsx(BreakdownEditorSelect, Object.assign({}, props, {
    clearable: minDimensions < 2,
    dropdownFooter: dropdownFooter,
    disabled: isDisabled,
    disabledReason: disabledReason,
    options: availablePropertiesOptionsWithoutDefaultMeasures,
    value: propertyValue,
    onChange: onBreakdownChange
  }));
};
export default RelationalReportBreakdownEditorDropdown;